import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface TourStep {
  target: string;
  title: string;
  description: string | React.ReactNode;
  position?: 'bottom' | 'top' | 'left' | 'right' | 'center';
}

interface MindmapTourOverlayProps {
  onClose: () => void;
}

const STEPS: TourStep[] = [
  {
    target: 'canvas',
    title: 'Welcome to Mindmap mode',
    position: 'center',
    description: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <p style={{ margin: 0 }}>Map out ideas with a visual mindmap. The central topic sits in the middle; all other nodes branch out from it.</p>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 12 }}>This tour walks you through the controls step by step.</p>
      </div>
    ),
  },
  {
    target: 'mm-btn-new',
    title: 'New mindmap',
    description: 'Start with a blank canvas. Your current work is not saved automatically — save first if you want to keep it.',
    position: 'bottom',
  },
  {
    target: 'mm-btn-open',
    title: 'Open',
    description: 'Browse and load all your saved mindmaps. Click a mindmap in the list to open it.',
    position: 'bottom',
  },
  {
    target: 'mm-btn-save',
    title: 'Save',
    description: 'Save the mindmap to the database. With Auto-save enabled this happens automatically after every change.',
    position: 'bottom',
  },
  {
    target: 'mm-btn-share',
    title: 'Share',
    description: 'Invite teammates by email or generate a shareable link. Available once the mindmap is saved.',
    position: 'bottom',
  },
  {
    target: 'mm-toggle-autosave',
    title: 'Auto-save',
    description: 'When enabled, changes are saved automatically as you work.',
    position: 'bottom',
  },
  {
    target: 'mm-zoom',
    title: 'Zoom',
    description: 'Zoom in or out with these buttons, or scroll the mouse wheel over the canvas.',
    position: 'bottom',
  },
  {
    target: 'canvas',
    title: 'Canvas controls',
    position: 'center',
    description: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { icon: '🖱️', keys: 'Double-click node', action: 'Edit text' },
          { icon: '🖱️', keys: 'Click + drag', action: 'Move node' },
          { icon: '🖱️', keys: 'Shift + drag', action: 'Move node to a different parent' },
          { icon: '🖱️', keys: 'Drag on empty canvas', action: 'Select multiple nodes' },
          { icon: '🖱️', keys: 'Scroll / right-drag', action: 'Zoom / pan' },
          { icon: '⌨️', keys: 'Tab', action: 'Create child node' },
          { icon: '⌨️', keys: 'Enter', action: 'Create sibling node' },
          { icon: '⌨️', keys: '↑ ↓ ← →', action: 'Navigate between nodes' },
          { icon: '⌨️', keys: 'Delete', action: 'Delete node' },
          { icon: '⌨️', keys: 'Ctrl + Z / Y', action: 'Undo / Redo' },
          { icon: '⌨️', keys: 'Ctrl + S', action: 'Save' },
        ].map(({ icon, keys, action }) => (
          <div key={keys} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
            <span style={{
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: 5,
              padding: '2px 7px',
              fontSize: 11,
              fontWeight: 600,
              color: '#374151',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>{keys}</span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{action}</span>
          </div>
        ))}
      </div>
    ),
  },
];

interface SpotlightRect { top: number; left: number; width: number; height: number; }
const PADDING = 6;

export default function MindmapTourOverlay({ onClose }: MindmapTourOverlayProps) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<SpotlightRect | null>(null);

  const currentStep = STEPS[step];

  const updateRect = useCallback(() => {
    if (currentStep.position === 'center') { setRect(null); return; }
    const el = document.querySelector(`[data-tour="${currentStep.target}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top - PADDING, left: r.left - PADDING, width: r.width + PADDING * 2, height: r.height + PADDING * 2 });
  }, [currentStep.target, currentStep.position]);

  useEffect(() => {
    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [updateRect]);

  const prev = () => setStep(s => Math.max(0, s - 1));
  const next = () => { if (step < STEPS.length - 1) setStep(s => s + 1); else onClose(); };

  const getTooltipStyle = (): React.CSSProperties => {
    const pos = currentStep.position ?? 'bottom';
    if (pos === 'center' || !rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 460 };
    const tooltipW = 340;
    const tooltipOffset = 16;
    switch (pos) {
      case 'bottom': return { top: rect.top + rect.height + tooltipOffset, left: Math.min(Math.max(rect.left + rect.width / 2 - tooltipW / 2, 8), window.innerWidth - tooltipW - 8), width: tooltipW };
      case 'top':    return { top: rect.top - tooltipOffset - 120, left: Math.min(Math.max(rect.left + rect.width / 2 - tooltipW / 2, 8), window.innerWidth - tooltipW - 8), width: tooltipW };
      case 'left':   return { top: rect.top + rect.height / 2 - 60, left: Math.max(rect.left - tooltipW - tooltipOffset, 8), width: tooltipW };
      case 'right':  return { top: rect.top + rect.height / 2 - 60, left: rect.left + rect.width + tooltipOffset, width: tooltipW };
    }
  };

  const getArrowClass = () => {
    const pos = currentStep.position ?? 'bottom';
    if (pos === 'center') return '';
    return pos === 'bottom' ? 'tour-arrow-up' : pos === 'top' ? 'tour-arrow-down' : pos === 'left' ? 'tour-arrow-right' : 'tour-arrow-left';
  };

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 9998, pointerEvents: 'none' }}>
        {rect ? (
          <>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: rect.top, background: 'rgba(0,0,0,0.55)' }} />
            <div style={{ position: 'absolute', top: rect.top + rect.height, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)' }} />
            <div style={{ position: 'absolute', top: rect.top, left: 0, width: rect.left, height: rect.height, background: 'rgba(0,0,0,0.55)' }} />
            <div style={{ position: 'absolute', top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height, background: 'rgba(0,0,0,0.55)' }} />
            <div style={{ position: 'absolute', top: rect.top, left: rect.left, width: rect.width, height: rect.height, borderRadius: 6, boxShadow: '0 0 0 2px #6366f1, 0 0 0 4px rgba(99,102,241,0.3)', transition: 'all 0.25s ease' }} />
          </>
        ) : (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} />
        )}
      </div>

      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, cursor: 'default' }} onClick={onClose} />

      <div style={{ position: 'fixed', zIndex: 10000, ...getTooltipStyle(), transition: 'top 0.25s ease, left 0.25s ease' }} onClick={e => e.stopPropagation()}>
        <style>{`
          .tour-arrow-up::before { content:''; position:absolute; top:-7px; left:24px; border:7px solid transparent; border-bottom-color:white; border-top:none; }
          .tour-arrow-down::before { content:''; position:absolute; bottom:-7px; left:24px; border:7px solid transparent; border-top-color:white; border-bottom:none; }
          .tour-arrow-right::before { content:''; position:absolute; right:-7px; top:20px; border:7px solid transparent; border-left-color:white; border-right:none; }
          .tour-arrow-left::before { content:''; position:absolute; left:-7px; top:20px; border:7px solid transparent; border-right-color:white; border-left:none; }
        `}</style>
        <div className={getArrowClass()} style={{ position: 'relative', background: 'white', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.1)', padding: '16px' }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2, borderRadius: 4, display: 'flex', alignItems: 'center' }}>
            <X size={14} />
          </button>
          <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 600, marginBottom: 4, letterSpacing: '0.05em' }}>
            STEP {step + 1} OF {STEPS.length}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 6, paddingRight: 20 }}>
            {currentStep.title}
          </div>
          <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.55, marginBottom: 14 }}>
            {currentStep.description}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {STEPS.map((_, i) => (
                <div key={i} onClick={() => setStep(i)} style={{ width: i === step ? 16 : 6, height: 6, borderRadius: 3, background: i === step ? '#6366f1' : '#e5e7eb', cursor: 'pointer', transition: 'all 0.2s' }} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {step > 0 && (
                <button onClick={prev} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '5px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: 'white', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                  <ChevronLeft size={13} /> Back
                </button>
              )}
              <button onClick={next} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '5px 12px', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg, #6366f1, #3b82f6)', fontSize: 12, fontWeight: 600, color: 'white', cursor: 'pointer' }}>
                {step < STEPS.length - 1 ? <>Next <ChevronRight size={13} /></> : 'Done ✓'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
